import type { Color } from "../common/color.js";
import { DataTexture, EquirectangularReflectionMapping, LinearFilter, RGBAFormat, SRGBColorSpace, UnsignedByteType } from "three/webgpu";

export interface StarfieldOptions {
  /** The same seed always paints the same sky. */
  seed?: number;
  width?: number;
  height?: number;
  /** Faint pinpricks. */
  stars?: number;
  /** Bright ones with a soft halo. */
  glowStars?: number;
  /** Deep space. Not quite black, because nothing in the sky is. */
  background?: Color;
}

export interface Pixels {
  data: Uint8Array;
  width: number;
  height: number;
}

/**
 * Paint an equirectangular star field.
 *
 * Pixels rather than a canvas, and no DOM anywhere: it runs headless, it is
 * testable, and it does not depend on how a browser happens to antialias an
 * arc. The layout is equirectangular, the top row the +Y pole, which is what
 * three expects of a `background`.
 *
 * Three details separate this from a field of identical dots.
 *
 * **Placement is uniform on the sphere, not on the image.** An equirectangular
 * image is enormously stretched at its poles, so scattering uniformly across
 * the *pixels* piles stars up at the top and bottom of the sky. Taking the
 * polar angle as `acos` of a uniform value undoes that exactly.
 *
 * **Radii are cubed.** A uniform radius gives a field that reads as noise;
 * cubing it produces mostly tiny pinpricks with a rare large one, which is
 * what a real sky looks like and what gives it depth.
 *
 * **Stars have color.** A fraction either side of white is warmed or cooled.
 * A pure grayscale field looks like dust on the lens.
 */
export function starfieldPixels(options: StarfieldOptions = {}): Pixels {
  const width = Math.max(2, Math.floor(options.width ?? 2048));
  const height = Math.max(2, Math.floor(options.height ?? 1024));
  const random = mulberry32(options.seed ?? 1337);
  const data = new Uint8Array(width * height * 4);

  const background = options.background ?? { r: 0.008, g: 0.012, b: 0.04, a: 1 };
  const br = to255(background.r);
  const bg = to255(background.g);
  const bb = to255(background.b);
  for (let i = 0; i < data.length; i += 4) {
    data[i] = br;
    data[i + 1] = bg;
    data[i + 2] = bb;
    data[i + 3] = 255;
  }

  /** A point uniform on the sphere, in image pixels. */
  const place = (): [number, number] => {
    const z = 2 * random() - 1;
    const azimuth = random();
    const polar = Math.acos(z);
    return [azimuth * width, (polar / Math.PI) * height];
  };

  const stars = options.stars ?? 2200;
  for (let i = 0; i < stars; i++) {
    const [x, y] = place();
    const radius = Math.pow(random(), 3) * 1.5 + 0.3;
    const brightness = 140 + Math.floor(random() * 115);
    const temperature = random();
    let r = brightness;
    let g = brightness;
    let b = brightness;
    if (temperature < 0.18) {
      g = Math.floor(brightness * 0.85);
      b = Math.floor(brightness * 0.7);
    } else if (temperature > 0.82) {
      r = Math.floor(brightness * 0.75);
      g = Math.floor(brightness * 0.86);
    }
    disc(data, width, height, x, y, radius, r, g, b, 0.5 + random() * 0.5, false);
  }

  const glow = options.glowStars ?? 70;
  for (let i = 0; i < glow; i++) {
    const [x, y] = place();
    const radius = 2 + random() * 2.5;
    // A wide soft halo around a hard core. These are also what a bloom pass
    // finds first, so they carry most of the sky's character once one is on.
    disc(data, width, height, x, y, radius * 4, 215, 228, 255, 0.5, true);
    disc(data, width, height, x, y, radius, 255, 255, 255, 0.95, false);
  }

  return { data, width, height };
}

/** The same field as a texture, ready to be a scene's `background`. */
export function starfieldTexture(options: StarfieldOptions = {}): DataTexture {
  const { data, width, height } = starfieldPixels(options);
  const texture = new DataTexture(data, width, height, RGBAFormat, UnsignedByteType);
  texture.name = "webwin.starfield";
  texture.mapping = EquirectangularReflectionMapping;
  texture.colorSpace = SRGBColorSpace;
  texture.magFilter = texture.minFilter = LinearFilter;
  // Row 0 is the top of the sky, as three's equirectangular mapping expects of an image.
  texture.flipY = false;
  texture.needsUpdate = true;
  return texture;
}

/**
 * Blend a filled circle over the image.
 *
 * `soft` fades from the center for the halo of a bright star; otherwise the
 * disc is solid with a one-pixel feathered rim, which is what keeps a
 * half-pixel star from either vanishing or looking like a square.
 *
 * Wraps horizontally, because the left and right edges of an equirectangular
 * image are the same meridian — a star that straddles it and does not wrap
 * leaves a visible vertical seam straight up the sky.
 */
function disc(
  data: Uint8Array,
  width: number,
  height: number,
  cx: number,
  cy: number,
  radius: number,
  r: number,
  g: number,
  b: number,
  alpha: number,
  soft: boolean,
): void {
  const minY = Math.max(0, Math.floor(cy - radius - 1));
  const maxY = Math.min(height - 1, Math.ceil(cy + radius + 1));
  const minX = Math.floor(cx - radius - 1);
  const maxX = Math.ceil(cx + radius + 1);

  for (let y = minY; y <= maxY; y++) {
    for (let x = minX; x <= maxX; x++) {
      const dx = x + 0.5 - cx;
      const dy = y + 0.5 - cy;
      const distance = Math.hypot(dx, dy);
      let coverage: number;
      if (soft) {
        coverage = Math.max(0, 1 - distance / radius);
        // Squared, so the halo falls away quickly and does not read as a disc.
        coverage *= coverage;
      } else {
        coverage = Math.min(1, Math.max(0, radius - distance + 0.5));
      }
      if (coverage <= 0) continue;

      const wrapped = ((x % width) + width) % width;
      const at = (y * width + wrapped) * 4;
      const a = coverage * alpha;
      data[at] = blend(data[at]!, r, a);
      data[at + 1] = blend(data[at + 1]!, g, a);
      data[at + 2] = blend(data[at + 2]!, b, a);
    }
  }
}

function blend(under: number, over: number, alpha: number): number {
  return Math.min(255, Math.round(under * (1 - alpha) + over * alpha));
}

function to255(v: number): number {
  return Math.max(0, Math.min(255, Math.round(v * 255)));
}

/**
 * mulberry32: small, fast, and good enough that a star field looks random.
 *
 * Written out rather than taken from anywhere, because a sky has to be
 * reproducible — the same seed must paint the same constellations on every
 * machine and in every future version, and a generator that came from the
 * platform could not promise that.
 */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
